import { Band, CarriedAdjustment, StatementLoan } from '../types/commission';

export { BONUS_SANDBOX_NOTE, SELF_GEN_BONUS_RULE } from './compPlan';

export const statementMeta = {
  loanOfficer: 'Alex Rivera',
  branch: 'Troy',
  period: 'October 2026',
  units: 7,
  volume: 2485400,
  adjustedVolume: 2232321.54,
};

export const statementOptions = {
  viewAs: [
    { value: 'self', label: 'Alex Rivera (my dashboard)' },
    { value: 'manager-az', label: 'Quinn Harper — Manager, Arizona only' },
  ],
  periods: ['October 2026', 'September 2026', 'August 2026'],
};

/** Troy Standard LO base BPS by the employee's period loan volume (values in $M). */
export const troyBaseBands: Band[] = [
  { label: 'Under $1.5M', from: 0, to: 1.5, valueLabel: '100 bps' },
  { label: '$1.5M or more', from: 1.5, to: null, valueLabel: '125 bps' },
];

export const BASE_RULES_SUMMARY = 'Troy 100, or 125 at ≥ $1.5M · Miami 100, or 150 at ≥ $2M · Arizona flat 100 bps';
export const BONUS_CAP_NOTE = 'Base + bonus can’t exceed 150 bps: bonus = MIN(bonus, 150 − base). Loan Officer fees only.';
export const TIER_LABEL_NOTE = 'Tier label (quarter snapshot) – display only, does not affect pay';

/** Monthly bonus BPS by count of closed loans in the month — Loan Officer fees only. Every band is set to main's 0. */
export const monthlyBonusBands: Band[] = [
  { label: '0–3 closed', from: 0, to: 4, valueLabel: '0 bps' },
  { label: '4–5 closed', from: 4, to: 6, valueLabel: '0 bps' },
  { label: '6 closed', from: 6, to: 7, valueLabel: '0 bps' },
  { label: '7+ closed', from: 7, to: null, valueLabel: '0 bps' },
];

/** All Correspondent, non-HELOC, divisor 3.25% (rate locked before the Troy cutoff). Base 125 (Troy ≥ $1.5M) + bonus 0 (main). */
export const statementLoans: StatementLoan[] = [
  {
    loanNumber: 'LN-2026-004655', borrower: 'M. Testerson', fundedDate: 'Oct 2 2026', loanAmount: 285000, adjustedAmount: 285000, baseBps: 125, bonusBps: 0,
    payTotal: 3562.5, adjustedTotal: 3562.5,
    lines: [
      { component: 'Base', ruleKey: 'V1-BT-TROY-2', bps: 125, payAmount: 3562.5, adjustedAmount: 3562.5 },
      { component: 'Monthly Bonus', ruleKey: 'V1-MB-3', bps: 0, payAmount: 0, adjustedAmount: 0 },
    ],
  },
  {
    loanNumber: 'LN-2026-004701', borrower: 'R. Placeholder', fundedDate: 'Oct 6 2026', loanAmount: 356200, adjustedAmount: 339760, baseBps: 125, bonusBps: 0,
    payTotal: 4452.5, adjustedTotal: 4247,
    lines: [
      { component: 'Base', ruleKey: 'V1-BT-TROY-2', bps: 125, payAmount: 4452.5, adjustedAmount: 4247 },
      { component: 'Monthly Bonus', ruleKey: 'V1-MB-3', bps: 0, payAmount: 0, adjustedAmount: 0 },
    ],
  },
  {
    loanNumber: 'LN-2026-004744', borrower: 'D. Example', fundedDate: 'Oct 9 2026', loanAmount: 198800, adjustedAmount: 0, baseBps: 125, bonusBps: 0,
    payTotal: 2485, adjustedTotal: 0, negativeComp: true,
    lines: [
      { component: 'Base', ruleKey: 'V1-BT-TROY-2', bps: 125, payAmount: 2485, adjustedAmount: 0 },
      { component: 'Monthly Bonus', ruleKey: 'V1-MB-3', bps: 0, payAmount: 0, adjustedAmount: 0 },
    ],
  },
  {
    loanNumber: 'LN-2026-004790', borrower: 'K. Mockley', fundedDate: 'Oct 12 2026', loanAmount: 524000, adjustedAmount: 512969.23, baseBps: 125, bonusBps: 0,
    payTotal: 3930, adjustedTotal: 3847.27, split: { pct: 60, partner: 'Jordan Lee', partnerPct: 40 },
    lines: [
      { component: 'Base', ruleKey: 'V1-BT-TROY-2', bps: 125, payAmount: 3930, adjustedAmount: 3847.27 },
      { component: 'Monthly Bonus', ruleKey: 'V1-MB-3', bps: 0, payAmount: 0, adjustedAmount: 0 },
    ],
  },
  {
    loanNumber: 'LN-2026-004812', borrower: 'J. Sample', fundedDate: 'Oct 14 2026', loanAmount: 412500, adjustedAmount: 385692.31, baseBps: 125, bonusBps: 0,
    payTotal: 5156.25, adjustedTotal: 4821.15,
    lines: [
      { component: 'Base', ruleKey: 'V1-BT-TROY-2', bps: 125, payAmount: 5156.25, adjustedAmount: 4821.15 },
      { component: 'Monthly Bonus', ruleKey: 'V1-MB-3', bps: 0, payAmount: 0, adjustedAmount: 0 },
    ],
  },
  {
    loanNumber: 'LN-2026-004859', borrower: 'T. Fakename', fundedDate: 'Oct 21 2026', loanAmount: 241600, adjustedAmount: 241600, baseBps: 125, bonusBps: 0,
    payTotal: 3020, adjustedTotal: 3020,
    lines: [
      { component: 'Base', ruleKey: 'V1-BT-TROY-2', bps: 125, payAmount: 3020, adjustedAmount: 3020 },
      { component: 'Monthly Bonus', ruleKey: 'V1-MB-3', bps: 0, payAmount: 0, adjustedAmount: 0 },
    ],
  },
  {
    loanNumber: 'LN-2026-004903', borrower: 'L. Demo', fundedDate: 'Oct 28 2026', loanAmount: 467300, adjustedAmount: 467300, baseBps: 125, bonusBps: 0,
    payTotal: 5841.25, adjustedTotal: 5841.25,
    lines: [
      { component: 'Base', ruleKey: 'V1-BT-TROY-2', bps: 125, payAmount: 5841.25, adjustedAmount: 5841.25 },
      { component: 'Monthly Bonus', ruleKey: 'V1-MB-3', bps: 0, payAmount: 0, adjustedAmount: 0 },
    ],
  },
];

export const carriedAdjustments: CarriedAdjustment[] = [
  {
    id: 'ADJ-2026-09-0117',
    sourcePeriod: 'September 2026',
    loanNumber: 'LN-2026-004398',
    borrower: 'P. Sampleton',
    reason: 'Loan amount corrected $310,000.00 → $318,000.00 after September locked. $8,000.00 × 125 bps = $100.00 (same on both bases)',
    postedOn: 'Oct 8 2026',
    amount: 100,
  },
  {
    id: 'ADJ-2026-08-0042',
    sourcePeriod: 'August 2026',
    loanNumber: 'LN-2026-003915',
    borrower: 'H. Testcase',
    reason:
      'Alex had 4 closed loans in August; a +25 bps bonus was paid in error and the correct bonus is 0 (main pays no standard bonus): $96,000.00 × 0.25% reversed = −$240.00',
    postedOn: 'Oct 4 2026',
    amount: -240,
  },
];
